import type { Session } from '@supabase/supabase-js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { getMe } from '../../lib/api';
import { supabase } from '../../lib/supabase';

interface SessionState {
  session: Session | null;
  /** True until the stored session has been read on startup. */
  loading: boolean;
}

const SessionContext = createContext<SessionState>({ session: null, loading: true });

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ session: null, loading: true });
  const queryClient = useQueryClient();

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setState({ session: data.session, loading: false }));

    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      setState({ session, loading: false });
      if (event === 'SIGNED_OUT') queryClient.clear();
      if (event === 'SIGNED_IN') queryClient.invalidateQueries({ queryKey: ['me'] });
    });
    return () => data.subscription.unsubscribe();
  }, [queryClient]);

  return <SessionContext.Provider value={state}>{children}</SessionContext.Provider>;
}

export function useSession() {
  return useContext(SessionContext);
}

export const meQueryKey = ['me'] as const;

/** The signed-in person's profile and verification status. */
export function useMe() {
  const { session } = useSession();
  return useQuery({
    queryKey: meQueryKey,
    queryFn: getMe,
    enabled: Boolean(session),
    staleTime: 60_000,
  });
}

export function signOut() {
  return supabase.auth.signOut();
}
